// RadarChart ViewModel - Calculate radar chart coordinates from responses

import {
  OnboardingQuestion,
  OnboardingResponse,
  ScoringCategory,
} from '../model/Onboarding.types';

export type RadarAxis = {
  id: string;
  name: string;
  angle: number; // Degrees (0-360)
  isHighlighted: boolean;
};

export type RadarPoint = {
  x: number;
  y: number;
  categoryId: string;
  categoryName: string;
  value: number; // chartValue 1-4
};

export type RadarRenderMode =
  | { type: 'empty' }
  | { type: 'single-point'; point: RadarPoint }
  | { type: 'line'; points: [RadarPoint, RadarPoint] }
  | { type: 'polygon'; points: RadarPoint[] };

export type RadarChartVM = {
  mode: RadarRenderMode;
  axes: RadarAxis[];
  gridLines: string[]; // Always 4 levels
  axisLines: Array<{ start: RadarPoint; end: RadarPoint }>;
  highlightedAxisId: string | null;
  size: number; // Chart size in pixels
  center: RadarPoint;
  maxRadius: number;
};

type UsedCategory = {
  id: string;
  name: string;
  questionIndices: number[];
};

const GRID_LEVELS = 4;

// Calculate angle for each axis (evenly distributed)
const calculateAxisAngle = (index: number, total: number): number => {
  if (total <= 0) return 270;

  // Start from top (270 degrees) and go clockwise
  const startAngle = 270;
  const angleStep = 360 / total;
  return (startAngle + index * angleStep) % 360;
};

const valueToRadius = (value: number, maxRadius: number): number => {
  return (value / 4) * maxRadius;
};

// Convert polar coordinates to cartesian
const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleDegrees: number,
): { x: number; y: number } => {
  const angleRadians = (angleDegrees * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleRadians),
    y: centerY + radius * Math.sin(angleRadians),
  };
};

const createCoordinatePoint = (x: number, y: number): RadarPoint => ({
  x,
  y,
  categoryId: 'center',
  categoryName: 'center',
  value: 0,
});

/**
 * Extracts unique categories actually used in questions.
 *
 * Only questions with non-null targetCategory are included.
 * Preserves the first-appearance order from the questions array.
 * Maps category IDs to display names using scoringCategories.
 *
 * @param questions - Onboarding questions in display order
 * @param scoringCategories - Category metadata for display names
 * @returns Ordered list of used categories
 */
const extractUsedCategories = (
  questions: OnboardingQuestion[],
  scoringCategories: ScoringCategory[],
): UsedCategory[] => {
  const categoryNameById = new Map(
    scoringCategories.map((category) => [category.id, category.name]),
  );
  const usedCategories = new Map<string, UsedCategory>();

  questions.forEach((question, index) => {
    if (!question.targetCategory) return;

    const existing = usedCategories.get(question.targetCategory);
    if (existing) {
      existing.questionIndices.push(index);
      return;
    }

    usedCategories.set(question.targetCategory, {
      id: question.targetCategory,
      name:
        categoryNameById.get(question.targetCategory) ??
        question.targetCategory,
      questionIndices: [index],
    });
  });

  return Array.from(usedCategories.values());
};

const findResponse = (
  questionId: string,
  localResponses: OnboardingResponse[],
  savedResponses: OnboardingResponse[],
): OnboardingResponse | null => {
  const local = localResponses.find(
    (response) => response.questionId === questionId,
  );
  if (local) return local;

  return (
    savedResponses.find((response) => response.questionId === questionId) ??
    null
  );
};

// Calculate score for a category based on responses
const calculateCategoryScore = (
  categoryId: string,
  questions: OnboardingQuestion[],
  localResponses: OnboardingResponse[],
  savedResponses: OnboardingResponse[],
  maxQuestionIndex: number,
): number | null => {
  const values: number[] = [];

  for (let index = 0; index < questions.length; index += 1) {
    if (index > maxQuestionIndex) break;

    const question = questions[index];
    if (question.targetCategory !== categoryId) continue;

    const response = findResponse(question.id, localResponses, savedResponses);
    if (!response?.answerId) continue;

    const answer = question.answers.find(
      (item) => item.id === response.answerId,
    );
    if (!answer) continue;

    values.push(answer.chartValue);
  }

  if (values.length === 0) return null;

  const total = values.reduce((sum, value) => sum + value, 0);
  return total / values.length;
};

/**
 * Determines the render mode based on how many categories are answered.
 *
 * - empty: 0 answered
 * - single-point: 1 answered
 * - line: 2 answered
 * - polygon: 3+ answered
 *
 * @param answeredPoints - Points for categories with answers
 * @returns Render mode ADT for the view
 */
const determineRenderMode = (answeredPoints: RadarPoint[]): RadarRenderMode => {
  if (answeredPoints.length === 0) {
    return { type: 'empty' };
  }

  if (answeredPoints.length === 1) {
    return { type: 'single-point', point: answeredPoints[0] };
  }

  if (answeredPoints.length === 2) {
    return {
      type: 'line',
      points: [answeredPoints[0], answeredPoints[1]],
    };
  }

  return { type: 'polygon', points: answeredPoints };
};

// Create RadarChartVM
// currentQuestionIndex limits which questions are considered (null = all)
export const createRadarChartVM = (
  questions: OnboardingQuestion[],
  localResponses: OnboardingResponse[],
  savedResponses: OnboardingResponse[],
  scoringCategories: ScoringCategory[],
  currentQuestionIndex: number | null,
  highlightedCategoryId: string | null,
  size = 300,
): RadarChartVM => {
  const usedCategories = extractUsedCategories(questions, scoringCategories);
  const axisCount = usedCategories.length;
  const maxQuestionIndex =
    currentQuestionIndex !== null ? currentQuestionIndex : questions.length - 1;
  const center = createCoordinatePoint(size / 2, size / 2);
  const maxRadius = (size / 2) * 0.8;

  const axes: RadarAxis[] = usedCategories.map((category, index) => ({
    id: category.id,
    name: category.name,
    angle: calculateAxisAngle(index, axisCount),
    isHighlighted: highlightedCategoryId === category.id,
  }));

  const answeredPoints: RadarPoint[] = [];
  axes.forEach((axis) => {
    const value = calculateCategoryScore(
      axis.id,
      questions,
      localResponses,
      savedResponses,
      maxQuestionIndex,
    );

    if (value === null) return;

    const radius = valueToRadius(value, maxRadius);
    const coords = polarToCartesian(center.x, center.y, radius, axis.angle);

    answeredPoints.push({
      x: coords.x,
      y: coords.y,
      categoryId: axis.id,
      categoryName: axis.name,
      value,
    });
  });

  const mode = determineRenderMode(answeredPoints);

  const axisLines = axes.map((axis) => {
    const endCoords = polarToCartesian(
      center.x,
      center.y,
      maxRadius,
      axis.angle,
    );
    return {
      start: center,
      end: {
        x: endCoords.x,
        y: endCoords.y,
        categoryId: axis.id,
        categoryName: axis.name,
        value: 0,
      },
    };
  });

  return {
    mode,
    axes,
    gridLines: generateGridLines(center, maxRadius, GRID_LEVELS, axisCount),
    axisLines,
    highlightedAxisId: highlightedCategoryId,
    size,
    center,
    maxRadius,
  };
};

// Helper to get axis label positions (outside the chart)
export const getAxisLabelPosition = (
  axis: RadarAxis,
  center: { x: number; y: number },
  maxRadius: number,
  labelOffset = 30,
): { x: number; y: number } => {
  return polarToCartesian(
    center.x,
    center.y,
    maxRadius + labelOffset,
    axis.angle,
  );
};

const createPolygonPath = (points: Array<{ x: number; y: number }>): string => {
  if (points.length === 0) return '';

  const pathParts = points.map((point, index) =>
    index === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`,
  );

  return `${pathParts.join(' ')} Z`;
};

/**
 * Generates grid lines for the radar chart (always 4 levels).
 *
 * Special cases:
 * - 2 axes or fewer: 4 horizontal lines
 * - 3 axes: 4 triangles
 * - 4+ axes: 4 regular polygons
 *
 * @param center - Chart center point
 * @param maxRadius - Max radius for the outer ring
 * @param levels - Number of grid levels (defaults to 4)
 * @param axisCount - Number of axes to render
 * @returns SVG path strings for each grid level
 */
export const generateGridLines = (
  center: { x: number; y: number },
  maxRadius: number,
  levels = GRID_LEVELS,
  axisCount = 0,
): string[] => {
  const paths: string[] = [];

  if (axisCount <= 2) {
    for (let level = 1; level <= levels; level += 1) {
      const radius = (level / levels) * maxRadius;
      const y = center.y + radius;
      const startX = center.x - maxRadius;
      const endX = center.x + maxRadius;
      paths.push(`M ${startX} ${y} L ${endX} ${y}`);
    }

    return paths;
  }

  for (let level = 1; level <= levels; level += 1) {
    const radius = (level / levels) * maxRadius;
    const points: Array<{ x: number; y: number }> = [];

    for (let i = 0; i < axisCount; i += 1) {
      const angle = calculateAxisAngle(i, axisCount);
      points.push(polarToCartesian(center.x, center.y, radius, angle));
    }

    paths.push(createPolygonPath(points));
  }

  return paths;
};

// Helper to generate axis lines from center to edge
export const generateAxisLines = (
  center: { x: number; y: number },
  maxRadius: number,
  axisCount = 0,
): Array<{ start: RadarPoint; end: RadarPoint }> => {
  const lines: Array<{ start: RadarPoint; end: RadarPoint }> = [];
  const start = createCoordinatePoint(center.x, center.y);

  for (let i = 0; i < axisCount; i += 1) {
    const angle = calculateAxisAngle(i, axisCount);
    const endCoords = polarToCartesian(center.x, center.y, maxRadius, angle);
    lines.push({
      start,
      end: {
        x: endCoords.x,
        y: endCoords.y,
        categoryId: `axis-${i}`,
        categoryName: `axis-${i}`,
        value: 0,
      },
    });
  }

  return lines;
};
